/**
 * Atalhos de filtro por vencimento, iguais em toda tela que filtre uma coluna de
 * validade.
 *
 * O nome do preset viaja na barra de endereco (`?vistoria=30dias`), entao ele e
 * parte do contrato de um link compartilhado: renomear `30dias` quebra favorito de
 * quem ja salvou. Por isso os valores sao curtos e estaveis, e o rotulo — que muda
 * sem custo — fica separado.
 *
 * O FILTRO E RESOLVIDO NO SERVIDOR, por faixa de data, e nao no navegador
 * (CLAUDE.md, "Regras de API", item 6: listagem sempre paginada no servidor).
 * `getFaixaDeVencimento` produz os limites que a consulta manda para a API;
 * `filtrarPorVencimento` existe para o caso em que a coluna nao e filtravel no
 * servidor e a pagina ja esta em maos.
 */

export type PresetDeVencimento = '' | 'vencido' | '30dias' | '60dias' | '90dias' | 'vigente';

export const OPCOES_DE_VENCIMENTO: readonly {
  value: Exclude<PresetDeVencimento, ''>;
  label: string;
}[] = [
  { value: 'vencido', label: 'Vencido' },
  { value: '30dias', label: 'Vence em 30 dias' },
  { value: '60dias', label: 'Vence em 60 dias' },
  { value: '90dias', label: 'Vence em 90 dias' },
  { value: 'vigente', label: 'Vigente' },
];

export interface FaixaDeVencimento {
  /** Maior ou igual a — inicio da faixa, em `YYYY-MM-DD`. */
  de?: string;
  /** Menor ou igual a — fim da faixa, em `YYYY-MM-DD`. */
  ate?: string;
  /** Estritamente menor que — usado por "vencido", que exclui o dia de hoje. */
  antesDe?: string;
}

/**
 * Data local em `YYYY-MM-DD`.
 *
 * NAO usa `toISOString()`: ele converte para UTC primeiro, e das 21h a meia-noite
 * em Sao Paulo isso devolve o dia SEGUINTE. O filtro "vencido" passaria a incluir
 * documentos que vencem amanha.
 */
function comoDiaLocal(data: Date): string {
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
}

function somarDias(base: Date, dias: number): Date {
  const data = new Date(base);
  data.setDate(data.getDate() + dias);
  return data;
}

export function getFaixaDeVencimento(preset: PresetDeVencimento): FaixaDeVencimento {
  if (!preset) {
    return {};
  }

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const hojeTexto = comoDiaLocal(hoje);

  switch (preset) {
    case 'vencido':
      return { antesDe: hojeTexto };
    case '30dias':
      return { de: hojeTexto, ate: comoDiaLocal(somarDias(hoje, 30)) };
    case '60dias':
      return { de: hojeTexto, ate: comoDiaLocal(somarDias(hoje, 60)) };
    case '90dias':
      return { de: hojeTexto, ate: comoDiaLocal(somarDias(hoje, 90)) };
    case 'vigente':
      return { de: hojeTexto };
    default:
      return {};
  }
}

/**
 * Filtra uma pagina ja em maos por uma coluna de validade.
 *
 * Registro SEM data fica de fora quando ha filtro: "vence em 30 dias" e uma
 * afirmacao sobre uma data, e quem nao tem data nao a satisfaz. Trazer os nulos
 * junto encheria o resultado de linhas que nao respondem a pergunta.
 */
export function filtrarPorVencimento<T>(
  itens: readonly T[],
  campo: keyof T,
  preset: PresetDeVencimento,
): T[] {
  if (!preset) {
    return [...itens];
  }
  const faixa = getFaixaDeVencimento(preset);
  return itens.filter((item) => cabeNaFaixa(item[campo], faixa));
}

/** Mesma ideia, mas basta UMA das colunas satisfazer — vencimento documental agregado. */
export function filtrarPorVencimentoEmQualquer<T>(
  itens: readonly T[],
  campos: readonly (keyof T)[],
  preset: PresetDeVencimento,
): T[] {
  if (!preset) {
    return [...itens];
  }
  const faixa = getFaixaDeVencimento(preset);
  return itens.filter((item) => campos.some((campo) => cabeNaFaixa(item[campo], faixa)));
}

function cabeNaFaixa(valor: unknown, faixa: FaixaDeVencimento): boolean {
  if (typeof valor !== 'string' || !valor) {
    return false;
  }
  // Comparacao de texto basta: `YYYY-MM-DD` ordena igual a data.
  if (faixa.antesDe && !(valor < faixa.antesDe)) {
    return false;
  }
  if (faixa.de && !(valor >= faixa.de)) {
    return false;
  }
  if (faixa.ate && !(valor <= faixa.ate)) {
    return false;
  }
  return true;
}
